/**
 * The tag and type filters (utils/category-facet-layout.ts) in one URL param,
 * `tf`: sections split by `;`, a section's key and values by `:`, values by `|`.
 *
 *   tf=species:dogs|cats;petAge:puppies;type:206
 *
 * One param, so every place that carries the filters (the drawer, the browse
 * and search pages, recent searches, /api/search/listings and /facets) passes
 * it through as a plain string. Values within a section match any; sections
 * must all match. Tag values are lowercase, as the index stores them; `type`
 * values are category ids.
 */

export type TagFacets = Record<string, string[]>;

const KEY = /^[a-zA-Z]{1,32}$/;

/** Can this be a value in the param? Tags holding a separator are left out of the filters. */
export const isFacetValue = (value: string): boolean => value.length > 0 && value.length <= 191 && !/[;:|]/.test(value);

/** The sections in a `tf` value; empty sections and bad keys dropped. Undefined when none is left. */
export function parseTagFacets(raw: unknown): TagFacets | undefined {
  const text = Array.isArray(raw) ? raw[0] : raw;
  if (typeof text !== 'string' || !text) return undefined;
  const out: TagFacets = {};
  for (const part of text.split(';')) {
    const at = part.indexOf(':');
    if (at < 1) continue;
    const key = part.slice(0, at).trim();
    if (!KEY.test(key)) continue;
    const values = part.slice(at + 1).split('|').map(v => v.trim().toLowerCase()).filter(isFacetValue);
    const kept = key === 'type' ? values.filter(v => /^\d{1,6}$/.test(v)) : values;
    if (kept.length) out[key] = [...new Set([...(out[key] ?? []), ...kept])];
  }
  return Object.keys(out).length ? out : undefined;
}

/** The `tf` value for these sections, keys and values sorted so one filter set is one string. Undefined when empty. */
export function encodeTagFacets(facets: TagFacets | undefined): string | undefined {
  if (!facets) return undefined;
  const parts = Object.keys(facets)
    .filter(k => KEY.test(k))
    .sort()
    .map(k => [k, [...new Set(facets[k].map(v => v.toLowerCase()).filter(isFacetValue))].sort()] as const)
    .filter(([, v]) => v.length)
    .map(([k, v]) => `${k}:${v.join('|')}`);
  return parts.length ? parts.join(';') : undefined;
}

/** `tf` with one value of one section added or taken out. Undefined when nothing is left. */
export function toggleTagFacet(raw: string | undefined, key: string, value: string): string | undefined {
  const facets = parseTagFacets(raw) ?? {};
  const v = value.toLowerCase();
  const now = facets[key] ?? [];
  facets[key] = now.includes(v) ? now.filter(x => x !== v) : [...now, v];
  return encodeTagFacets(facets);
}

/** How many sections have a value. */
export const tagFacetCount = (raw: string | undefined): number => Object.keys(parseTagFacets(raw) ?? {}).length;
